import { motion } from "motion/react";
import { Link } from "react-router";
import {
  ArrowRight,
  CheckCircle2,
  Clock3,
  FileText,
  Globe,
  Search,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { getAbsoluteUrl } from "../site";

export function ProfessionelleWebsiteOhneAufwand() {
  const painPoints = [
    {
      title: "Technik wird zum Nebenschauplatz",
      description:
        "Viele kleine Unternehmen wollen online professionell auftreten, aber keinen laufenden Website-Betrieb intern organisieren.",
    },
    {
      title: "Pflege wird oft unterschätzt",
      description:
        "Nach dem Launch beginnen Updates, kleine Änderungen, Sicherheitsfragen und die laufende inhaltliche Pflege.",
    },
    {
      title: "Zu viele Einzelzuständigkeiten",
      description:
        "Wenn Erstellung, Hosting, Wartung und spätere Anpassungen getrennt laufen, kostet selbst eine kleine Änderung oft unnötig Zeit.",
    },
  ];

  const pillars = [
    {
      icon: <Sparkles className="h-5 w-5" />,
      title: "Professioneller Auftritt",
      description:
        "Eine Website soll seriös wirken, das Angebot klar erklären und auf allen Geräten sauber funktionieren.",
    },
    {
      icon: <ShieldCheck className="h-5 w-5" />,
      title: "Laufender Betrieb",
      description:
        "Hosting, Sicherheitsupdates und technische Pflege gehören bei einer Unternehmenswebsite dauerhaft dazu.",
    },
    {
      icon: <Wrench className="h-5 w-5" />,
      title: "Praktische Betreuung",
      description:
        "Wirklich entlastend ist eine Website erst dann, wenn spätere Änderungen nicht jedes Mal ein neues Projekt auslösen.",
    },
    {
      icon: <Search className="h-5 w-5" />,
      title: "SEO als Grundlage",
      description:
        "Klare Struktur, passende Inhalte und eine saubere technische Basis helfen dabei, überhaupt sichtbar zu werden.",
    },
  ];

  const included = [
    "Individuelle Unternehmenswebsite statt austauschbarem Standard-Template",
    "Responsive Darstellung für Smartphone, Tablet und Desktop",
    "Hosting, Wartung, Sicherheit und laufende technische Pflege",
    "Klare Zuständigkeiten für Betrieb und spätere Änderungen",
    "Saubere Grundlage für lokale Sichtbarkeit und verständliche Inhalte",
    "Ein Modell, das Website-Erstellung und Website-Betrieb zusammen denkt",
  ];

  const steps = [
    {
      title: "Anforderungen klären",
      description:
        "Im Fragebogen werden Angebot, Zielgruppe, Inhalte und der gewünschte Auftritt strukturiert erfasst.",
    },
    {
      title: "Entwurf erhalten",
      description:
        "Auf dieser Basis entsteht ein erster Vorschlag, der das Unternehmen verständlich und professionell präsentiert.",
    },
    {
      title: "Finalisieren und veröffentlichen",
      description:
        "Nach Feedback wird die Website fertiggestellt und sauber live geschaltet.",
    },
    {
      title: "Laufend betreuen",
      description:
        "Nach dem Launch bleiben Hosting, Pflege, Sicherheit und spätere Anpassungen in einem betreuten Rahmen.",
    },
  ];

  const seoQuestions = [
    {
      question: "Was bedeutet professionelle Website ohne Aufwand?",
      answer:
        "Gemeint ist eine Website-Lösung, bei der nicht nur Design und technische Umsetzung betrachtet werden, sondern auch Betrieb, Wartung und spätere Pflege mitgedacht sind.",
    },
    {
      question: "Was ist der Unterschied zu einem klassischen Webdesign-Projekt?",
      answer:
        "Ein klassisches Projekt endet oft nach dem Launch oder trennt spätere Pflege von der Erstellung. Ein WaaS-Modell verbindet beides stärker und reduziert dadurch Reibung im Alltag.",
    },
    {
      question: "Warum ist das für SEO und Sichtbarkeit relevant?",
      answer:
        "SEO profitiert von klaren Leistungsseiten, aktueller Technik, funktionierenden Kontaktwegen und gepflegten Inhalten. Genau diese Punkte leiden oft, wenn nach dem Launch keine klare Betreuung existiert.",
    },
  ];

  const faq = [
    {
      question: "Ist Website as a Service nur ein anderes Wort für Website-Flatrate?",
      answer:
        "Nicht unbedingt. Entscheidend ist nicht das Schlagwort, sondern der tatsächliche Leistungsrahmen. Gemeint ist eine Website, bei der Erstellung, Betrieb und laufende Betreuung sinnvoll zusammengedacht werden.",
    },
    {
      question: "Für wen ist eine professionelle Website ohne Aufwand sinnvoll?",
      answer:
        "Vor allem für kleine Unternehmen, B2B-Dienstleister, Praxen, Kanzleien und Gründer, die professionell auftreten möchten, aber keine eigene Website- oder IT-Struktur aufbauen wollen.",
    },
    {
      question: "Geht es dabei nur um Technik?",
      answer:
        "Nein. Neben Technik geht es auch um Inhalte, Ansprechpartner, Aktualität, Suchmaschinen-Grundlagen und die Frage, wie Änderungen im Alltag praktisch abgewickelt werden.",
    },
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Professionelle Website ohne Aufwand",
      url: getAbsoluteUrl("/professionelle-website-ohne-aufwand"),
      description:
        "Professionelle Website ohne Aufwand: Erklärung des WaaS-Modells, laufender Website-Betrieb, SEO-Basis und betreute Unternehmenswebsite.",
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Professionelle Website ohne Aufwand",
      serviceType: "Website as a Service",
      provider: {
        "@type": "Organization",
        name: "mutzur",
        url: getAbsoluteUrl("/"),
      },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: "500",
        description:
          "Professionelle Website mit laufender Betreuung, Hosting, Wartung und technischem Betrieb.",
      },
      url: getAbsoluteUrl("/professionelle-website-ohne-aufwand"),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [...seoQuestions, ...faq].map((entry) => ({
        "@type": "Question",
        name: entry.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: entry.answer,
        },
      })),
    },
  ];

  return (
    <div className="overflow-hidden bg-stone-50 pt-20 text-stone-950">
      <Seo
        title="Professionelle Website ohne Aufwand"
        description="Professionelle Website ohne Aufwand: Erklärung des WaaS-Modells, laufender Website-Betrieb, SEO-Basis und betreute Unternehmenswebsite."
        path="/professionelle-website-ohne-aufwand"
        keywords={[
          "professionelle website ohne aufwand",
          "website as a service",
          "waas website",
          "website betreuung",
          "website wartung inklusive",
          "website betrieb",
        ]}
        structuredData={structuredData}
      />

      <section className="relative border-b border-stone-200 bg-[linear-gradient(180deg,#fcfbf8_0%,#f6f2ea_100%)]">
        <div className="absolute inset-0 opacity-70">
          <div className="absolute left-[-8rem] top-10 h-72 w-72 rounded-full bg-amber-100 blur-3xl" />
          <div className="absolute right-[-6rem] top-20 h-80 w-80 rounded-full bg-stone-200 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr),360px] lg:items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-4 py-2 text-sm text-stone-700 shadow-sm">
                <Sparkles className="h-4 w-4" />
                Website as a Service für kleine Unternehmen
              </div>

              <h1 className="mt-6 max-w-5xl text-5xl leading-[0.96] tracking-tight sm:text-6xl lg:text-7xl">
                Professionelle Website
                <span className="block text-stone-500">ohne unnötigen Aufwand</span>
              </h1>

              <p className="mt-6 max-w-3xl text-lg leading-relaxed text-stone-700 sm:text-xl">
                Eine professionelle Website ist für viele Unternehmen wichtig. Der eigentliche
                Aufwand entsteht aber oft nicht bei der Entscheidung für eine Website, sondern
                danach: bei Technik, Wartung, Pflege und späteren Änderungen.
              </p>

              <p className="mt-4 max-w-3xl text-base leading-relaxed text-stone-600">
                Genau hier setzt Website as a Service an. Das Modell verbindet Erstellung,
                laufenden Betrieb und praktische Betreuung, damit die Website nicht zum
                Nebenthema mit zu vielen offenen Zuständigkeiten wird.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/blog/was-ist-waas"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-stone-900 px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-stone-800"
                >
                  Mehr zu WaaS lesen
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/fragebogen"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-300 bg-white px-7 py-3.5 text-sm font-medium text-stone-900 transition-colors hover:border-stone-400 hover:bg-stone-100"
                >
                  Unverbindlich anfragen
                </Link>
              </div>
            </motion.div>

            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.1 }}
              className="rounded-[2rem] border border-stone-200 bg-white/85 p-6 shadow-sm backdrop-blur"
            >
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Kurz erklärt</p>
              <h2 className="mt-4 text-2xl leading-tight">Worauf es bei einer Website ohne Aufwand ankommt</h2>
              <div className="mt-6 space-y-4">
                {[
                  "klare Inhalte und professioneller Auftritt",
                  "laufender technischer Betrieb statt bloßer Übergabe",
                  "weniger Abstimmung bei späteren Änderungen",
                  "solide Grundlage für Sichtbarkeit und SEO",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm leading-relaxed text-stone-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[320px,minmax(0,1fr)] lg:items-start">
            <div className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Ausgangslage</p>
              <h2 className="mt-4 text-3xl leading-tight">Warum viele Unternehmen das Thema Website aufschieben</h2>
              <p className="mt-4 text-sm leading-relaxed text-stone-700">
                Nicht weil eine Website unwichtig wäre, sondern weil nach dem Launch häufig
                unklar bleibt, wer Inhalte, Technik und spätere Pflege zuverlässig übernimmt.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {painPoints.map((item, index) => (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.07 }}
                  className="rounded-[2rem] border border-stone-200 bg-[#faf8f3] p-6 shadow-sm"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-stone-200 text-stone-700">
                    <Clock3 className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-xl leading-tight">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-stone-700">{item.description}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-stone-200 bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Was bedeutet das?</p>
            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Website as a Service erklärt
            </h2>
            <p className="mt-5 text-base leading-relaxed text-stone-700 sm:text-lg">
              Website as a Service, oft als WaaS abgekürzt, beschreibt ein Modell, bei dem
              eine Website nicht nur als einmaliges Design- oder Entwicklungsprojekt betrachtet
              wird. Stattdessen werden Website-Erstellung, Hosting, Wartung, Sicherheit und
              laufende Betreuung zusammen gedacht.
            </p>
            <p className="mt-4 text-base leading-relaxed text-stone-700 sm:text-lg">
              Für kleine Unternehmen ist genau das häufig entscheidend. Eine Unternehmenswebsite
              muss nicht nur online gehen, sondern auch aktuell, sicher, verständlich und
              verlässlich nutzbar bleiben. Ohne klare Betreuung wird genau dieser Teil schnell
              zum Engpass.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {pillars.map((pillar, index) => (
              <motion.article
                key={pillar.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="rounded-[2rem] border border-stone-200 bg-stone-50 p-6 shadow-sm"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-stone-800 shadow-sm">
                  {pillar.icon}
                </div>
                <h3 className="mt-5 text-xl leading-tight">{pillar.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-700">{pillar.description}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-stone-50 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),340px]">
            <article className="rounded-[2rem] border border-stone-200 bg-white p-7 shadow-sm sm:p-8">
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500">SEO und Sichtbarkeit</p>
              <h2 className="mt-4 text-3xl leading-tight sm:text-4xl">
                Warum eine betreute Website auch für SEO sinnvoll ist
              </h2>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-stone-700">
                <p>
                  SEO ist nicht nur eine Frage von Keywords. Sichtbarkeit entsteht dann, wenn
                  Leistungsseiten verständlich aufgebaut sind, Inhalte aktuell bleiben, Kontaktwege
                  funktionieren und die technische Basis sauber gepflegt wird.
                </p>
                <p>
                  Genau an diesen Punkten verlieren viele Unternehmenswebsites nach dem Launch an
                  Qualität. Inhalte veralten, kleine Fehler bleiben liegen, neue Leistungen werden
                  nicht sauber ergänzt und technische Details geraten aus dem Blick.
                </p>
                <p>
                  Eine Website ohne Aufwand bedeutet deshalb auch: weniger Reibung bei Pflege und
                  Aktualisierung. Und das ist indirekt oft ein sehr praktischer SEO-Vorteil.
                </p>
              </div>
            </article>

            <aside className="rounded-[2rem] border border-stone-200 bg-[#f5efe4] p-6 shadow-sm">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-stone-800 shadow-sm">
                <Globe className="h-5 w-5" />
              </div>
              <h3 className="mt-5 text-2xl leading-tight">Was meist dazugehört</h3>
              <div className="mt-5 space-y-3">
                {included.map((item) => (
                  <div key={item} className="flex items-start gap-3 text-sm leading-relaxed text-stone-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="border-y border-stone-200 bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[360px,minmax(0,1fr)]">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Ablauf</p>
              <h2 className="mt-4 text-4xl leading-tight">
                Wie eine Website mit laufender Betreuung typischerweise funktioniert
              </h2>
              <p className="mt-5 text-base leading-relaxed text-stone-700">
                Damit eine professionelle Website ohne Aufwand realistisch wird, braucht es
                nicht weniger Struktur, sondern bessere Struktur.
              </p>
            </div>

            <div className="space-y-4">
              {steps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: 14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: index * 0.05 }}
                  className="grid gap-4 rounded-[2rem] border border-stone-200 bg-stone-50 p-5 shadow-sm sm:grid-cols-[72px,minmax(0,1fr)] sm:p-6"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-lg text-stone-900 shadow-sm">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="text-xl leading-tight">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-stone-700">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-stone-50 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Häufige Suchfragen</p>
            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Was viele Unternehmen zu WaaS und Website-Betreuung wissen wollen
            </h2>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {seoQuestions.map((entry, index) => (
              <motion.article
                key={entry.question}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-stone-100">
                  <Search className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-xl leading-tight">{entry.question}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-700">{entry.answer}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-stone-200 bg-[#f5efe4] py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr),300px] lg:items-start">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Weiterführend</p>
              <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
                Mehr zum Thema Website as a Service
              </h2>
              <p className="mt-5 max-w-3xl text-base leading-relaxed text-stone-700">
                Wer das Modell genauer einordnen möchte, findet in den beiden Beiträgen weitere
                Hintergründe zu WaaS, Website-Betrieb und der Frage, warum kleine Unternehmen oft
                weniger ein reines Webdesign-Projekt als eine betreute Lösung brauchen.
              </p>
            </div>

            <aside className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm">
              <p className="text-sm leading-relaxed text-stone-600">
                Preise spielen natürlich eine Rolle, sind hier aber nicht der Hauptpunkt.
                Wichtiger ist meist, ob das Modell organisatorisch zum Unternehmen passt.
              </p>
            </aside>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            <Link
              to="/blog/professionelle-website-ohne-aufwand"
              className="group rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm transition-all hover:border-stone-300 hover:bg-stone-100"
            >
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Blogbeitrag</p>
              <h3 className="mt-4 text-3xl leading-tight">Professionelle Website ohne Aufwand</h3>
              <p className="mt-4 text-sm leading-relaxed text-stone-700">
                Warum kleine Unternehmen weniger ein Webdesign-Projekt als ein verlässliches
                Website-System brauchen.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-stone-900">
                Beitrag lesen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/blog/was-ist-waas"
              className="group rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm transition-all hover:border-stone-300 hover:bg-stone-100"
            >
              <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Blogbeitrag</p>
              <h3 className="mt-4 text-3xl leading-tight">Was ist WaaS?</h3>
              <p className="mt-4 text-sm leading-relaxed text-stone-700">
                Eine kompakte Einordnung von Website as a Service, inklusive Abgrenzung zu
                Baukasten, Freelancer und Agentur.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-stone-900">
                Begriff verstehen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] text-stone-500">FAQ</p>
            <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
              Weitere Fragen zur Website-Betreuung
            </h2>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {faq.map((entry, index) => (
              <motion.article
                key={entry.question}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: index * 0.05 }}
                className="rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-stone-100">
                  <FileText className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-xl leading-tight">{entry.question}</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-700">{entry.answer}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-stone-200 bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-sm uppercase tracking-[0.2em] text-stone-500">Nächster Schritt</p>
          <h2 className="mt-4 text-4xl leading-tight sm:text-5xl">
            Wenn Sie prüfen möchten, ob das Modell zu Ihrem Unternehmen passt
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-stone-700">
            Dann lässt sich das über den Fragebogen schnell und unverbindlich einordnen. Wenn Sie
            sich zuerst weiter informieren möchten, sind die Beiträge zu WaaS und Website-Betrieb
            der bessere Einstieg.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/fragebogen"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-stone-900 px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-stone-800"
            >
              Fragebogen starten
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/blog"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-stone-300 bg-white px-7 py-3.5 text-sm font-medium text-stone-900 transition-colors hover:border-stone-400 hover:bg-stone-100"
            >
              Erst weitere Beiträge lesen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
